(() => {
  const selectors = {
    wrapper: '[data-petlux-review-carousel]',
    root: '.junip-review-section',
    frame: '.junip-reviews-container',
    item: '.junip-review-list-item-container',
    previous: '.petlux-review-carousel-control--previous',
    next: '.petlux-review-carousel-control--next'
  };

  const instances = new WeakMap();

  class PetluxVerticalReviewCarousel {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.frame = null;
      this.items = [];
      this.currentIndex = 0;
      this.timer = null;
      this.refreshQueued = false;
      this.isPaused = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.observer = new MutationObserver(() => this.queueRefresh());
      this.observer.observe(wrapper, { childList: true, subtree: true });

      wrapper.addEventListener('mouseenter', () => this.pause());
      wrapper.addEventListener('mouseleave', () => this.resume());
      wrapper.addEventListener('focusin', () => this.pause());
      wrapper.addEventListener('focusout', (event) => {
        if (!wrapper.contains(event.relatedTarget)) this.resume();
      });
      wrapper.addEventListener('touchstart', () => this.pause(), { passive: true });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });

      window.addEventListener('resize', () => this.queueUpdate());
      this.refresh();
    }

    queueRefresh() {
      if (this.refreshQueued) return;
      this.refreshQueued = true;
      window.requestAnimationFrame(() => {
        this.refreshQueued = false;
        this.refresh();
      });
    }

    refresh() {
      const root = this.wrapper.querySelector(selectors.root);
      const frame = root?.querySelector(selectors.frame);
      if (!frame) return;

      const items = Array.from(frame.children).filter((child) => child.matches(selectors.item));
      if (!items.length) return;

      this.frame = frame;
      this.items = items;
      this.currentIndex = Math.min(this.currentIndex, items.length - 1);
      this.wrapper.classList.add('is-petlux-carousel-ready');
      this.wrapper.classList.toggle('is-single-review', items.length === 1);
      const controls = this.ensureControls(frame);
      this.bindControl(controls.previous, -1);
      this.bindControl(controls.next, 1);
      this.update(false);
      this.startAutoplay();
    }

    ensureControls(frame) {
      const parent = frame.parentElement;
      let previous = parent.querySelector(selectors.previous);
      let next = parent.querySelector(selectors.next);

      if (!previous) {
        previous = document.createElement('button');
        previous.type = 'button';
        previous.className = 'petlux-review-carousel-control petlux-review-carousel-control--previous';
        parent.insertBefore(previous, frame);
      }

      if (!next) {
        next = document.createElement('button');
        next.type = 'button';
        next.className = 'petlux-review-carousel-control petlux-review-carousel-control--next';
        parent.insertBefore(next, frame.nextSibling);
      }

      return { previous, next };
    }

    bindControl(control, direction) {
      if (!control || control.dataset.petluxVerticalBound === 'true') return;
      control.dataset.petluxVerticalBound = 'true';
      control.setAttribute('aria-label', direction < 0 ? 'Previous review' : 'Next review');

      const move = (event) => {
        if (this.items.length < 2) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.goTo(this.currentIndex + direction, true);
      };

      control.addEventListener('click', move, true);
      control.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') move(event);
      }, true);
    }

    goTo(index, restartAutoplay = false) {
      if (!this.items.length) return;
      this.currentIndex = (index + this.items.length) % this.items.length;
      this.update(true);
      if (restartAutoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    queueUpdate() {
      window.requestAnimationFrame(() => this.update(false));
    }

    update(animate) {
      if (!this.frame || !this.items.length) return;
      const activeItem = this.items[this.currentIndex];

      this.items.forEach((item, index) => {
        const isActive = index === this.currentIndex;
        item.inert = !isActive;
        if (isActive) item.removeAttribute('aria-hidden');
        else item.setAttribute('aria-hidden', 'true');
      });

      window.requestAnimationFrame(() => {
        const firstTop = this.items[0].offsetTop;
        const activeTop = activeItem.offsetTop - firstTop;
        this.frame.style.height = `${activeItem.offsetHeight}px`;
        this.frame.scrollTo({
          top: activeTop,
          behavior: animate && !this.reducedMotion.matches ? 'smooth' : 'auto'
        });
      });
    }

    pause() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    resume() {
      this.isPaused = false;
      this.startAutoplay();
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.isPaused || document.hidden || this.reducedMotion.matches || this.items.length < 2) return;
      this.timer = window.setInterval(() => this.goTo(this.currentIndex + 1), 7000);
    }

    stopAutoplay() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(selectors.wrapper).forEach((wrapper) => {
      if (instances.has(wrapper)) return;
      instances.set(wrapper, new PetluxVerticalReviewCarousel(wrapper));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
